import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import nogruntlogo from "../../Assets/nogrunt-logo.svg";
import { sendUserInfo } from "./../../API/UserApi";
import { getCompanyName } from "../../API/CreateCollectionAPI";
import { CommonMethods } from "./../../components/common/CommonMethods";
import { HelpIconButton } from "../../components-new/help/help-modal";
import { Chrome, Youtube } from "lucide-react";
import { useNotification } from "../../hooks";
import { getBrandLogo } from "../../utils/userDetailsUtils";
import { encryptPassword } from "../../utils/cryptoUtils";

const SignUpPage = () => {
  const notify = useNotification();
  const [account, setAccount] = useState({
    status: 0,
    password: "",
    email: "",
    firstname: "",
    lastname: "",
    company: "",
    phonenumber: "",
    confirmpassword: "",
    product: "",
    module: "",
    plan: "",
    techstack: "",
    optForPageObject: false,
    terms: false,
  });
  const navigate = useNavigate();
  const [focusedField, setFocusedField] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const [companyName, setCompanyName] = useState([]);
  const [showModuleProduct, setShowModuleProduct] = useState(true);
  const logoToShow = getBrandLogo(nogruntlogo);

  const location = useLocation();
  const path = location.pathname;
  const cname = searchParams.get("cname");
  const codegen = searchParams.get("codegen");
  const plan = searchParams.get("plan");
  const techstack = searchParams.get("techstack");

  useEffect(() => {
    if (cname !== null) {
      getCompanyNameFromUrl(cname);
    }
    setAccount({ ...account, plan: plan, techstack: techstack });
  }, [cname, codegen, plan, techstack]);

  const getCompanyNameFromUrl = async (id) => {
    await getCompanyName(id)
      .then((response) => {
        if (response.status === 200) {
          if (response.data.message === "fail") {
            CommonMethods(navigate);
          } else {
            setCompanyName(response.data);
            setShowModuleProduct(false);
          }
        }
      })
      .catch(() => {});
  };

  const handelAccount = (property, event) => {
    let value = event.target.value;
    if (property === "phonenumber") {
      value = value.replace(/[^0-9\-\s]/g, "");
    }
    setAccount({ ...account, [property]: value });
  };

  const validateEmail = (email) => {
    return email.match(
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
  };

  const validatePassword = (password) => {
    return password.match(
      /^(?!.*\s)(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[~`!@#$%^&*()--+={}[\]|\\:;"'<>,.?/_₹]).{8,16}$/,
    );
  };

  const validateName = (name) => {
    return name.match(/^[a-zA-Z\s\-'.]{1,50}$/);
  };

  const validatePhoneNumber = (phonenumber) => {
    return phonenumber.match(/^(\d{3})[- ]?(\d{3})[- ]?(\d{4})$/);
  };

  const validateTextField = (value) => {
    return value.match(/^[a-zA-Z0-9\s\-_.]{1,100}$/);
  };

  const createUser = async (account) => {
    if (
      account.password === "" ||
      account.email === "" ||
      account.firstname === "" ||
      account.lastname === "" ||
      account.phonenumber === "" ||
      account.confirmpassword === ""
    ) {
      notify("Please fill in all required fields.", "error");
      return;
    } else if (
      cname === null &&
      codegen === null &&
      (account.company === "" || account.product === "" || account.module === "")
    ) {
      notify("Please fill in company, product, and module fields.", "error");
      return;
    }

    if (account.confirmpassword !== account.password) {
      notify("Password and confirm password do not match.", "error");
      return;
    } else if (!validateName(account.firstname)) {
      notify("Please enter a valid first name.", "error");
      return;
    } else if (!validateName(account.lastname)) {
      notify("Please enter a valid last name.", "error");
      return;
    } else if (account.company && !validateTextField(account.company)) {
      notify("Company name contains invalid characters.", "error");
      return;
    } else if (account.product && !validateTextField(account.product)) {
      notify("Product name contains invalid characters.", "error");
      return;
    } else if (account.module && !validateTextField(account.module)) {
      notify("Module name contains invalid characters.", "error");
      return;
    } else if (!validateEmail(account.email)) {
      notify("Please enter a valid email address.", "error");
      return;
    } else if (!validatePassword(account.password)) {
      notify(
        "Password must be 8-16 chars with uppercase, lowercase, number, and special character.",
        "error",
      );
      return;
    }

    let company = "";
    let companyid = null;

    if (cname != null && typeof cname !== "undefined" && cname !== "") {
      company = companyName.companyname;
      companyid = companyName.companyId;
    } else {
      company = account.company;
    }

    setIsLoading(true);
    try {
      const encryptedPassword = await encryptPassword(account.password);
      await sendUserInfo({
        status: account.status,
        password: encryptedPassword,
        email: account.email,
        firstname: account.firstname,
        lastname: account.lastname,
        company: company,
        companyid: companyid,
        phonenumber: account.phonenumber,
        product: account.product,
        module: account.module,
      })
        .then((response) => {
          if (response.status === 200) {
            const authData = response.data?.response ?? response.data;

            localStorage.setItem(
              "userDetail",
              JSON.stringify({
                email: account.email,
                ...authData,
                companyid: authData?.company?.idcompany,
                usertype: authData?.usertype,
                token: authData?.randomkey,
              }),
            );
            localStorage.setItem(
              "colorcode",
              JSON.stringify({
                primaryColor: authData?.company?.primaryColor,
                secondaryColor: authData?.company?.secondaryColor,
                tertiaryColor: authData?.company?.tertiaryColor,
                brandLogo: authData?.company?.brandLogo,
                companyurl: authData?.company?.companyurl,
              }),
            );
            setAccount({
              status: 0,
              password: "",
              email: "",
              firstname: "",
              lastname: "",
              company: "",
              phonenumber: "",
              confirmpassword: "",
              product: "",
              module: "",
            });
            notify("Account created successfully!", "success");
            navigate("/new", { replace: true });
          }
        })
        .catch(() => {});
    } catch (error) {
      notify("Sign up failed. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const createUserFromurl = async (account) => {
    if (
      account.password === "" ||
      account.email === "" ||
      account.firstname === "" ||
      account.lastname === "" ||
      account.phonenumber === "" ||
      account.confirmpassword === ""
    ) {
      notify("Please fill in all required fields.", "error");
      return;
    }

    if (account.confirmpassword !== account.password) {
      notify("Password and confirm password do not match.", "error");
      return;
    } else if (!validateName(account.firstname)) {
      notify("Please enter a valid first name.", "error");
      return;
    } else if (!validateName(account.lastname)) {
      notify("Please enter a valid last name.", "error");
      return;
    } else if (!validateEmail(account.email)) {
      notify("Please enter a valid email address.", "error");
      return;
    } else if (!validatePassword(account.password)) {
      notify(
        "Password must be 8-16 chars with uppercase, lowercase, number, and special character.",
        "error",
      );
      return;
    }

    setIsLoading(true);
    try {
      const encryptedPassword = await encryptPassword(account.password);
      await sendUserInfo({
        status: account.status,
        password: encryptedPassword,
        email: account.email,
        firstname: account.firstname,
        lastname: account.lastname,
        company: companyName.companyname,
        companyid: companyName.companyId,
        phonenumber: account.phonenumber,
      })
        .then((response) => {
          if (response.status === 200) {
            let user = {
              email: account.email,
              firstname: account.firstname,
              lastname: account.lastname,
              company: companyName.companyname,
              phonenumber: account.phonenumber,
            };

            let json = JSON.stringify(user);
            localStorage.setItem("userDetail", json);
            setAccount({
              status: 0,
              password: "",
              email: "",
              firstname: "",
              lastname: "",
              company: "",
              phonenumber: "",
              confirmpassword: "",
            });
            notify("Account created successfully!", "success");
            navigate("/LoginPage");
          }
        })
        .catch(() => {});
    } catch (error) {
      notify("Sign up failed. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const createCodegenUser = async (account) => {
    if (
      account.password === "" ||
      account.email === "" ||
      account.firstname === "" ||
      account.lastname === "" ||
      account.techstack === "" ||
      account.plan === "" ||
      account.company === "" ||
      account.phonenumber === "" ||
      account.confirmpassword === ""
    ) {
      notify("Please fill in all required fields.", "error");
      return;
    }

    if (account.confirmpassword !== account.password) {
      notify("Password and confirm password do not match.", "error");
      return;
    } else if (!validateName(account.firstname)) {
      notify("Please enter a valid first name.", "error");
      return;
    } else if (!validateName(account.lastname)) {
      notify("Please enter a valid last name.", "error");
      return;
    } else if (!validateTextField(account.company)) {
      notify("Company name contains invalid characters.", "error");
      return;
    } else if (!validateEmail(account.email)) {
      notify("Please enter a valid email address.", "error");
      return;
    } else if (!validatePassword(account.password)) {
      notify(
        "Password must be 8-16 chars with uppercase, lowercase, number, and special character.",
        "error",
      );
      return;
    }

    setIsLoading(true);
    try {
      const encryptedPassword = await encryptPassword(account.password);
      await sendUserInfo({
        status: account.status,
        password: encryptedPassword,
        email: account.email,
        firstname: account.firstname,
        lastname: account.lastname,
        company: account.company,
        companyid: account.companyId,
        phonenumber: account.phonenumber,
        gentype: "code",
        codegen: codegen,
        techstack: account.techstack,
        plan: account.plan,
        optForPageObject: account.optForPageObject,
      })
        .then((response) => {
          if (response.status === 200) {
            let user = {
              email: account.email,
              firstname: account.firstname,
              lastname: account.lastname,
              company: account.company,
              phonenumber: account.phonenumber,
              gentype: "code",
              codegen: codegen,
              techstack: account.techstack,
              plan: account.plan,
              optForPageObject: account.optForPageObject,
            };

            let json = JSON.stringify(user);
            localStorage.setItem("userDetail", json);
            localStorage.setItem("payment_session_id", response.data.paymentSessionId);
            localStorage.removeItem("payment_completed");
            setAccount({
              status: 0,
              password: "",
              email: "",
              firstname: "",
              lastname: "",
              company: "",
              phonenumber: "",
              confirmpassword: "",
              techstack: "",
              plan: "",
            });
            notify("Account created successfully!", "success");
            navigate("/payment");
          }
        })
        .catch(() => {});
    } catch (error) {
      notify("Sign up failed. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to get input className
  const getInputClassName = (field, error, disabled) => {
    let base =
      "w-full px-4 py-3 border-2 rounded-xl outline-none transition-all duration-200 text-sm";
    if (disabled) {
      return `${base} bg-gray-100 cursor-not-allowed text-gray-500 border-gray-200`;
    }
    if (error) {
      return `${base} border-red-300 bg-red-50`;
    }
    if (focusedField === field) {
      return `${base} border-blue-500 bg-white`;
    }
    return `${base} border-gray-200 bg-gray-50 hover:border-gray-300`;
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 px-4 py-6">
      <div className="w-full max-w-5xl">
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-5">
            {/* Left Panel - Promotional Content */}
            <div
              className="lg:col-span-2 p-8"
              style={{
                background: "linear-gradient(135deg, #242f3f 0%, #1a2332 100%)",
              }}
            >
              <div className="h-full flex flex-col">
                {/* Logo */}
                <div className="mb-8">
                  <img src={logoToShow} alt="Logo" className="h-10 brightness-0 invert" />
                </div>

                {/* Title */}
                <div className="mb-8">
                  <h1
                    className="text-2xl font-bold text-white mb-2"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    Start automating your tests today
                  </h1>
                  <p
                    className="text-gray-300 text-sm"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    Join thousands of QA teams using Nogrunt to ship faster with confidence.
                  </p>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col gap-3 mt-auto">
                  <a
                    href="https://chromewebstore.google.com/detail/nogrunt-qa-automation/neankkcidljlljifcclgcecadeimmolp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors cursor-pointer"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: "#4285F4" }}
                    >
                      <Chrome className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-white">Get Chrome Extension</div>
                      <div className="text-gray-400 text-xs">
                        Record tests directly in your browser
                      </div>
                    </div>
                  </a>
                  <a
                    href="https://www.youtube.com/watch?v=B4Q1pDSIsXk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors cursor-pointer"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: "#FF0000" }}
                    >
                      <Youtube className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-white">Watch Demo</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Right Panel - Form */}
            <div className="lg:col-span-3 p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2
                    className="text-xl font-bold text-gray-900"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    Create your account
                  </h2>
                  <p
                    className="text-gray-500 text-sm"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    Fill in the details below to get started
                  </p>
                </div>
                <HelpIconButton path={path} />
              </div>

              {/* Form */}
              <div className="space-y-4">
                {/* Name Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2"
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    >
                      First name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={account.firstname}
                      onChange={(e) => handelAccount("firstname", e)}
                      placeholder="Enter first name"
                      onFocus={() => setFocusedField("firstname")}
                      onBlur={() => setFocusedField(null)}
                      className={getInputClassName(
                        "firstname",
                        !validateName(account.firstname) && account.firstname.length > 0,
                      )}
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    />
                    {!validateName(account.firstname) && account.firstname.length > 0 && (
                      <p
                        className="text-red-500 text-xs mt-1"
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      >
                        Invalid first name
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2"
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    >
                      Last name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={account.lastname}
                      onChange={(e) => handelAccount("lastname", e)}
                      placeholder="Enter last name"
                      onFocus={() => setFocusedField("lastname")}
                      onBlur={() => setFocusedField(null)}
                      className={getInputClassName(
                        "lastname",
                        !validateName(account.lastname) && account.lastname.length > 0,
                      )}
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    />
                    {!validateName(account.lastname) && account.lastname.length > 0 && (
                      <p
                        className="text-red-500 text-xs mt-1"
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      >
                        Invalid last name
                      </p>
                    )}
                  </div>
                </div>

                {/* Company & Phone Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2"
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    >
                      Company name <span className="text-red-500">*</span>
                    </label>
                    {cname !== null ? (
                      <input
                        type="text"
                        value={companyName.companyname || ""}
                        disabled
                        className={getInputClassName("company", false, true)}
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      />
                    ) : (
                      <input
                        type="text"
                        required
                        value={account.company}
                        onChange={(e) => handelAccount("company", e)}
                        placeholder="Enter company name"
                        onFocus={() => setFocusedField("company")}
                        onBlur={() => setFocusedField(null)}
                        className={getInputClassName(
                          "company",
                          !validateTextField(account.company) && account.company.length > 0,
                        )}
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      />
                    )}
                    {cname === null &&
                      !validateTextField(account.company) &&
                      account.company.length > 0 && (
                        <p
                          className="text-red-500 text-xs mt-1"
                          style={{ fontFamily: "Urbanist, sans-serif" }}
                        >
                          Invalid characters in company name
                        </p>
                      )}
                  </div>
                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2"
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    >
                      Phone number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={account.phonenumber}
                      onChange={(e) => handelAccount("phonenumber", e)}
                      placeholder="Enter phone number"
                      onFocus={() => setFocusedField("phonenumber")}
                      onBlur={() => setFocusedField(null)}
                      className={getInputClassName(
                        "phonenumber",
                        !validatePhoneNumber(account.phonenumber) && account.phonenumber.length > 0,
                      )}
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    />
                    {!validatePhoneNumber(account.phonenumber) &&
                      account.phonenumber.length > 0 && (
                        <p
                          className="text-red-500 text-xs mt-1"
                          style={{ fontFamily: "Urbanist, sans-serif" }}
                        >
                          Enter a valid phone number
                        </p>
                      )}
                  </div>
                </div>

                {/* Email Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2"
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    >
                      Email address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={account.email}
                      onChange={(e) => handelAccount("email", e)}
                      placeholder="Enter your email"
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      className={getInputClassName(
                        "email",
                        !validateEmail(account.email) && account.email.length > 0,
                      )}
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    />
                    {!validateEmail(account.email) && account.email.length > 0 && (
                      <p
                        className="text-red-500 text-xs mt-1"
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      >
                        Enter a valid email
                      </p>
                    )}
                  </div>
                  {codegen !== null && (
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-2"
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      >
                        Pricing plan
                      </label>
                      <input
                        type="text"
                        value={plan || ""}
                        disabled
                        className={getInputClassName("plan", false, true)}
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      />
                    </div>
                  )}
                </div>

                {/* Codegen specific fields */}
                {codegen !== null && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-2"
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      >
                        Tech stack
                      </label>
                      <input
                        type="text"
                        value={techstack || ""}
                        disabled
                        className={getInputClassName("techstack", false, true)}
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      />
                    </div>
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-2"
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      >
                        Page Object Model
                      </label>
                      <div className="flex gap-4 py-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="optForPageObject"
                            checked={account.optForPageObject === true}
                            onChange={() => setAccount({ ...account, optForPageObject: true })}
                            className="w-4 h-4 text-blue-600 cursor-pointer"
                          />
                          <span
                            className="text-sm text-gray-700"
                            style={{ fontFamily: "Urbanist, sans-serif" }}
                          >
                            Yes
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="optForPageObject"
                            checked={account.optForPageObject === false}
                            onChange={() => setAccount({ ...account, optForPageObject: false })}
                            className="w-4 h-4 text-blue-600 cursor-pointer"
                          />
                          <span
                            className="text-sm text-gray-700"
                            style={{ fontFamily: "Urbanist, sans-serif" }}
                          >
                            No
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Password Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2"
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    >
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      required
                      value={account.password}
                      onChange={(e) => handelAccount("password", e)}
                      placeholder="Enter password"
                      onFocus={() => setFocusedField("password")}
                      onBlur={() => setFocusedField(null)}
                      className={getInputClassName(
                        "password",
                        !validatePassword(account.password) && account.password.length > 0,
                      )}
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    />
                    {!validatePassword(account.password) && account.password.length > 0 && (
                      <p
                        className="text-red-500 text-xs mt-1"
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      >
                        Password doesn't meet requirements
                      </p>
                    )}
                    <p
                      className="text-xs text-gray-400 mt-1"
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    >
                      8-16 chars, uppercase, lowercase, number, special char
                    </p>
                  </div>
                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2"
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    >
                      Confirm password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      required
                      value={account.confirmpassword}
                      onChange={(e) => handelAccount("confirmpassword", e)}
                      placeholder="Confirm password"
                      onFocus={() => setFocusedField("confirmpassword")}
                      onBlur={() => setFocusedField(null)}
                      className={getInputClassName(
                        "confirmpassword",
                        account.password !== account.confirmpassword &&
                          account.confirmpassword.length > 0,
                      )}
                      style={{ fontFamily: "Urbanist, sans-serif" }}
                    />
                    {account.password !== account.confirmpassword &&
                      account.confirmpassword.length > 0 && (
                        <p
                          className="text-red-500 text-xs mt-1"
                          style={{ fontFamily: "Urbanist, sans-serif" }}
                        >
                          Passwords don't match
                        </p>
                      )}
                  </div>
                </div>

                {/* Product & Module (when no cname or codegen) */}
                {cname === null && codegen === null && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-2"
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      >
                        Product name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={account.product}
                        onChange={(e) => handelAccount("product", e)}
                        placeholder="Enter product name"
                        onFocus={() => setFocusedField("product")}
                        onBlur={() => setFocusedField(null)}
                        className={getInputClassName(
                          "product",
                          !validateTextField(account.product) && account.product.length > 0,
                        )}
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      />
                      {!validateTextField(account.product) && account.product.length > 0 && (
                        <p
                          className="text-red-500 text-xs mt-1"
                          style={{ fontFamily: "Urbanist, sans-serif" }}
                        >
                          Invalid characters in product name
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-2"
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      >
                        Module name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={account.module}
                        onChange={(e) => handelAccount("module", e)}
                        placeholder="Enter module name"
                        onFocus={() => setFocusedField("module")}
                        onBlur={() => setFocusedField(null)}
                        className={getInputClassName(
                          "module",
                          !validateTextField(account.module) && account.module.length > 0,
                        )}
                        style={{ fontFamily: "Urbanist, sans-serif" }}
                      />
                      {!validateTextField(account.module) && account.module.length > 0 && (
                        <p
                          className="text-red-500 text-xs mt-1"
                          style={{ fontFamily: "Urbanist, sans-serif" }}
                        >
                          Invalid characters in module name
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <label className="flex items-start gap-3 cursor-pointer group mt-2">
                  <input
                    type="checkbox"
                    required
                    checked={account.terms}
                    onChange={() => setAccount({ ...account, terms: !account.terms })}
                    className="w-5 h-5 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <span
                    className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                  >
                    I agree to the{" "}
                    <a
                      href="https://www.nogrunt.com/terms/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://www.nogrunt.com/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>

                {/* Submit Button */}
                <button
                  onClick={() => {
                    if (codegen === null) {
                      createUser(account);
                    } else {
                      if (cname === undefined) {
                        createUserFromurl(account);
                      } else {
                        createCodegenUser(account);
                      }
                    }
                  }}
                  disabled={!account.terms || isLoading}
                  className={`w-full py-3.5 font-semibold rounded-xl transition-all duration-200 cursor-pointer ${
                    !account.terms || isLoading
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Creating account...
                    </span>
                  ) : (
                    "Create account"
                  )}
                </button>

                {/* Sign In Link */}
                <p
                  className="text-center text-gray-500"
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                >
                  Already have an account?{" "}
                  <button
                    onClick={() => navigate("/LoginPage")}
                    className="text-blue-600 hover:text-blue-700 font-semibold transition-colors cursor-pointer"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
